import { createFileRoute, Link } from "@tanstack/react-router";
import { useMutation, useSuspenseQuery, useQueryClient } from "@tanstack/react-query";
import { Suspense, useEffect } from "react";
import { useServerFn } from "@tanstack/react-start";
import { Loader2, Plus, Search, Trash2, X } from "lucide-react";
import { toast } from "sonner";

import { format } from "date-fns";

import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { adminDeleteContent, adminListContent } from "@/lib/admin.functions";
import { CONTENT_KINDS, type ContentKind } from "@/lib/cms.types";
import { LOCALES } from "@/lib/i18n";
import { AdminPagination } from "@/components/admin/AdminPagination";
import { adminPageCount } from "@/lib/admin-pagination";
import { contentSearchUpdate, parseAdminContentSearch } from "@/lib/admin-content-search";

const LABELS: Record<ContentKind, string> = {
  blog: "Blog articles",
  news: "News",
  case: "Case studies",
  industry: "Industry pages",
  integration: "Integrations",
};

export const Route = createFileRoute("/admin/content/$kind/")({
  staticData: { sitemap: false },
  validateSearch: parseAdminContentSearch,
  head: ({ params }) => ({ meta: [
    { title: `${LABELS[(CONTENT_KINDS as readonly string[]).includes(params.kind) ? params.kind as ContentKind : "blog"]} admin | factory.online` },
    { name: "description", content: "Manage factory.online content and publication status." },
    { property: "og:title", content: `Content administration: ${params.kind} | factory.online` },
    { property: "og:description", content: "Manage factory.online content and publication status." },
    { property: "og:type", content: "website" },
    { name: "twitter:card", content: "summary" },
  ] }),
  component: ContentListPage,
});

function ContentListPage() {
  const { q } = Route.useSearch();
  const navigate = Route.useNavigate();
  return <>
    <form className="mb-6 flex items-center gap-2" onSubmit={(event) => {
      event.preventDefault();
      const value = new FormData(event.currentTarget).get("q");
      void navigate({ search: (prev) => ({ ...prev, ...contentSearchUpdate(String(value ?? "")) }) });
    }}>
      <Input key={q} name="q" type="search" defaultValue={q} maxLength={200} placeholder="Search titles or slugs…" aria-label="Search content" className="max-w-md" />
      <Button type="submit" variant="outline" size="icon" aria-label="Search" title="Search"><Search aria-hidden /></Button>
      {q ? <Button type="button" variant="ghost" size="icon" aria-label="Clear search" title="Clear search" onClick={() => void navigate({ search: (prev) => ({ ...prev, ...contentSearchUpdate("") }) })}><X aria-hidden /></Button> : null}
    </form>
    <Suspense fallback={<div className="mt-10 flex justify-center"><Loader2 className="size-6 animate-spin text-primary" aria-label="Loading content" /></div>}><ContentList /></Suspense>
  </>;
}

function ContentList() {
  const { kind } = Route.useParams();
  const { page, pageSize, q } = Route.useSearch();
  const navigate = Route.useNavigate();
  const typedKind = (CONTENT_KINDS as readonly string[]).includes(kind)
    ? (kind as ContentKind)
    : "blog";
  const list = useServerFn(adminListContent);
  const remove = useServerFn(adminDeleteContent);
  const queryClient = useQueryClient();

  const { data } = useSuspenseQuery({
    queryKey: ["admin-content", typedKind, page, pageSize, q],
    queryFn: () => list({ data: { kind: typedKind, page, pageSize, q: contentSearchUpdate(q).q } }),
    staleTime: 30_000,
  });
  const pages = adminPageCount(data.total, pageSize);
  useEffect(() => {
    if (page > pages) void navigate({ search: (prev) => ({ ...prev, page: pages }), replace: true });
  }, [page, pages, navigate]);

  const deleteMutation = useMutation({
    mutationFn: (id: string) => remove({ data: { id } }),
    onSuccess: () => {
      toast.success("Deleted");
      void queryClient.invalidateQueries({ queryKey: ["admin-content", typedKind] });
    },
    onError: (error: Error) => toast.error(error.message),
  });

  return (
    <>
      <div className="flex flex-wrap items-center justify-between gap-4">
        <h1 className="font-display text-3xl font-bold">{LABELS[typedKind]}</h1>
        <Button asChild>
          <Link
            to="/admin/content/$kind/$id"
            params={{ kind: typedKind, id: "new" }}
          >
            <Plus className="size-4" aria-hidden />
            New entry
          </Link>
        </Button>
      </div>

      {!data.rows.length ? (
        <p className="mt-10 text-sm text-muted-foreground">{q ? "No matching entries." : "Nothing here yet."}</p>
      ) : (
        <div className="mt-8 overflow-x-auto rounded-lg border border-border">
          <table className="w-full text-left text-sm">
            <thead className="bg-surface text-xs tracking-wide text-muted-foreground uppercase">
              <tr>
                <th className="p-4">Title</th>
                <th className="p-4">Slug</th>
                <th className="p-4">Date</th>
                <th className="p-4">Languages</th>
                <th className="p-4">Status</th>
                <th className="p-4" />
              </tr>
            </thead>
            <tbody>
              {data.rows.map((row) => {
                const en = row.content_translations?.find((t) => t.locale === "en");
                const filled = (row.content_translations ?? []).filter((t) =>
                  t.title?.trim(),
                ).length;
                return (
                  <tr key={row.id} className="border-t border-border">
                    <td className="p-4 font-medium">
                      <Link
                        to="/admin/content/$kind/$id"
                        params={{ kind: typedKind, id: row.id }}
                        className="hover:text-primary"
                      >
                        {en?.title?.trim() || row.slug}
                      </Link>
                    </td>
                    <td className="p-4 text-muted-foreground">{row.slug}</td>
                    <td className="p-4 whitespace-nowrap text-muted-foreground">
                      {row.published_at ? format(new Date(row.published_at), "d MMM yyyy") : "—"}
                    </td>
                    <td className="p-4 text-muted-foreground">
                      {filled}/{LOCALES.length}
                    </td>
                    <td className="p-4">
                      <Badge variant={row.published ? "default" : "secondary"}>
                        {row.published ? "Published" : "Draft"}
                      </Badge>
                    </td>
                    <td className="p-4 text-right">
                      <Button
                        size="icon"
                        variant="ghost"
                        aria-label={`Delete ${row.slug}`}
                        onClick={() => {
                          if (confirm(`Delete "${row.slug}"? This cannot be undone.`)) {
                            deleteMutation.mutate(row.id);
                          }
                        }}
                      >
                        <Trash2 className="size-4" aria-hidden />
                      </Button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      <AdminPagination page={page} pageSize={pageSize} total={data.total}
        onPageChange={(next) => void navigate({ search: (prev) => ({ ...prev, page: next }) })}
        onPageSizeChange={(size) => void navigate({ search: (prev) => ({ ...prev, page: 1, pageSize: size }) })}
      />
    </>
  );
}
